import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { createAppSocket } from '@/lib/socket';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '@/stores/authStore';
import { playNotificationSound } from '@/utils/notificationSound';
import { useNotificationModal } from '@/contexts/NotificationModalContext';

export interface Notification {
  id: string;
  type: 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS' | 'URGENT';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  title: string;
  message: string;
  userId?: string;
  role?: 'OPERADOR' | 'GESTOR' | 'ADMIN';
  companyId?: string;
  actionUrl?: string;
  expiresAt?: string;
  readAt?: string;
  createdAt: string;
  sound?: boolean;
}

export interface NotificationData {
  type: 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS' | 'URGENT';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  title: string;
  message: string;
  userId?: string;
  role?: 'OPERADOR' | 'GESTOR' | 'ADMIN';
  companyId?: string;
  actionUrl?: string;
  sound?: boolean;
}

export const useNotifications = () => {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isConnected, setIsConnected] = useState(false);
  const { openCardModal, openGestorModal } = useNotificationModal();

  // Função para processar ação da notificação
  const handleNotificationAction = useCallback((notification: Notification) => {
    if (!notification.actionUrl) return;

    // Extrair clienteId da URL se existir
    const clienteMatch = notification.actionUrl.match(/cliente=([^&]+)/);
    const clienteId = clienteMatch ? clienteMatch[1] : null;
    const feedbackMatch = notification.actionUrl.match(/feedbackId=([^&]+)/);
    const feedbackId = feedbackMatch ? decodeURIComponent(feedbackMatch[1]) : null;

    // Determinar qual modal abrir baseado na URL
    if (notification.actionUrl.includes('/gestor-cobrancas')) {
      navigate('/gestor-cobrancas');
      openGestorModal(feedbackId);
    } else if (clienteId && notification.actionUrl.includes('/kanban')) {
      openCardModal(clienteId);
    }
  }, [openCardModal, openGestorModal, navigate]);

  // Buscar notificações do usuário
  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await api.get('/notifications');
      return response.data.data.notifications as Notification[];
    },
    enabled: !!user,
  });

  // Contar notificações não lidas
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const response = await api.get('/notifications/unread-count');
      return response.data.data.count as number;
    },
    enabled: !!user,
  });

  // Marcar notificação como lida
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await api.patch(`/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  // Marcar todas como lidas
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      await api.patch('/notifications/mark-all-read');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  // Deletar notificação
  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await api.delete(`/notifications/${notificationId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  // Enviar notificação (apenas para ADMIN)
  const sendNotificationMutation = useMutation({
    mutationFn: async (data: NotificationData) => {
      const response = await api.post('/notifications/send', data);
      return response.data.data.notification;
    },
    onSuccess: () => {
      toast.success('Notificação enviada com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao enviar notificação');
    },
  });

  // Função para mostrar notificação push do navegador
  const showPushNotification = useCallback((notification: Notification) => {
    if (!('Notification' in window)) {
      return;
    }

    if (Notification.permission === 'granted') {
      const pushNotification = new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: notification.id,
        requireInteraction: notification.priority === 'URGENT',
      });

      // Adicionar evento de clique na notificação push
      pushNotification.onclick = () => {
        handleNotificationAction(notification);
        pushNotification.close();
      };
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          const pushNotification = new Notification(notification.title, {
            body: notification.message,
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            tag: notification.id,
            requireInteraction: notification.priority === 'URGENT',
          });

          // Adicionar evento de clique na notificação push
          pushNotification.onclick = () => {
            handleNotificationAction(notification);
            pushNotification.close();
          };
        }
      });
    }
  }, [handleNotificationAction]);

  const showPushNotificationRef = useRef(showPushNotification);
  showPushNotificationRef.current = showPushNotification;

  // Configurar WebSocket
  useEffect(() => {
    if (!user?.id) {
      return;
    }

    const newSocket = createAppSocket();
    const userId = user.id;

    newSocket.on('connect', () => {
      setIsConnected(true);
      newSocket.emit('authenticate', { token: userId });
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    newSocket.on('connect_error', () => {
      setIsConnected(false);
    });

    newSocket.on('notification', (notification: Notification) => {
      queryClient.setQueryData(['notifications'], (old: Notification[] = []) => {
        return [notification, ...old];
      });

      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });

      toast(notification.message, {
        icon: notification.type === 'URGENT' ? '🚨' :
              notification.type === 'SUCCESS' ? '✅' :
              notification.type === 'WARNING' ? '⚠️' : 'ℹ️',
        duration: notification.priority === 'URGENT' ? 8000 : 4000,
      });

      if (notification.sound) {
        playNotificationSound();
      }

      showPushNotificationRef.current(notification);
    });

    return () => {
      newSocket.disconnect();
      setIsConnected(false);
    };
  }, [user?.id, queryClient]);

  // Solicitar permissão para notificações push
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      // Solicitar permissão quando o usuário fizer alguma ação
      const requestPermission = () => {
        Notification.requestPermission();
        document.removeEventListener('click', requestPermission);
      };
      
      document.addEventListener('click', requestPermission);
      
      return () => {
        document.removeEventListener('click', requestPermission);
      };
    }
  }, []);

  return {
    notifications,
    unreadCount,
    isLoading,
    isConnected,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    deleteNotification: deleteNotificationMutation.mutate,
    sendNotification: sendNotificationMutation.mutate,
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
    isDeleting: deleteNotificationMutation.isPending,
    isSending: sendNotificationMutation.isPending,
    handleNotificationAction,
  };
};
